import { ReactElement, useMemo, useState } from 'react';

import { StandardsByTrackModal } from '@backrs_ui/common-ui/src/molecules/AssessMyTrack/StandardsByTrackModal';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, Button, Divider, SxProps, Theme } from '@mui/material';

import { useAcademicStandards } from '../hooks/useAcademicStandards';

const STANDARDS_STRINGS = appStrings.modules.assessMyTrack.standardsByTrack;

export const StandardsByTrackCard = (): ReactElement => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTenure, setSelectedTenure] = useState<string | undefined>(undefined);

  const { category: academicFoundationsCategory, filteredStandards } =
    useAcademicStandards(selectedTenure);

  // stable identity: the modal resets its selected column when the variant changes
  const variant = useMemo(
    () => ({ kind: 'by-track' as const, standards: filteredStandards ?? [] }),
    [filteredStandards],
  );

  const handleOpenModal = (tenure: string) => {
    setSelectedTenure(tenure);
    setModalOpen(true);
  };

  return (
    <>
      <Box sx={containerSx}>
        <Box sx={contentSx}>
          <Box sx={titleContainerSx}>
            <Typography variant="text-lg-semibold">{STANDARDS_STRINGS.title}</Typography>
            <Typography
              variant="text-sm-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {STANDARDS_STRINGS.description}
            </Typography>
          </Box>
        </Box>
        <Divider sx={dividerSx} />
        <Box sx={footerSx}>
          <Button
            hierarchy="Secondary gray"
            size="sm"
            onClick={() => handleOpenModal('High School')}
            id="standards-by-track-high-school-btn"
          >
            {STANDARDS_STRINGS.highSchoolStandards}
          </Button>
          <Button
            hierarchy="Secondary gray"
            size="sm"
            onClick={() => handleOpenModal('Post High School')}
            id="standards-by-track-college-btn"
          >
            {STANDARDS_STRINGS.collegeStandards}
          </Button>
        </Box>
      </Box>

      {academicFoundationsCategory && (
        <StandardsByTrackModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          category={academicFoundationsCategory}
          variant={variant}
        />
      )}
    </>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    padding: backrs.spacing['spacing-3xl'],
  },
  paddingBottom: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
});

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
  marginTop: 'auto',
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    padding: backrs.spacing['spacing-3xl'],
  },
  paddingTop: backrs.spacing['spacing-2xl'],
});
